import React, { useEffect, useState } from 'react'
import { Button, Nav } from 'react-bootstrap'
import { FaClock, FaRegSquarePlus } from "react-icons/fa6";
import toast from 'react-hot-toast'
import CustomisedModal from '../../components/Modal/CustomisedModal';
import { Box, Paper, Tooltip } from "@mui/material";
import DataTable from "react-data-table-component";
import { FiClock, FiCheckCircle, FiXCircle, FiTrash2, FiNavigation, FiCalendar, FiList, FiFile, FiFilePlus } from 'react-icons/fi';
import { getAllNotification, scheduleAutoNotification, toggleAutoNotificationStatus } from '../../api/context/autoNotifications';
import Loader from '../../components/loader/loader';
import BulkNotificationModal from './AutoNotificationModal';
import CreateUpdateAutoNotificationModal from './CreateUpdateAutoNotificationModal';
import { Switch } from '@mui/material';

const queueDatatableCustomStyles = {
    headCells: {
        style: {
            display: "flex",
            justifyContent: "center",
            fontWeight: '600',
            fontSize: '14px',
            padding: '16px 8px',
            backgroundColor: '#f9f9f9ff',
        },
    },
    cells: {
        style: {
            display: "flex",
            fontSize: '12px',
            fontWeight: '500',
            padding: '12px',
        },
    },
};

const AutoNotification = () => {
    const [data, setData] = useState([]);
    const [update, setUpdate] = useState(false)
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [initial, setInitial] = useState(false)
    const [deleteModal, setDeleteModal] = useState(false);
    const [deletingId, setDeletingId] = useState("");
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('DESC');
    const [activeTab, setActiveTab] = useState('upcoming'); // Default to upcoming
    const [isInitialRender, setIsInitialRender] = useState(true);
    const [showBulkModal, setShowBulkModal] = useState(false);

    // Unified modal state
    const [notificationModal, setNotificationModal] = useState(false);
    const [modalMode, setModalMode] = useState('view'); // 'view', 'edit', 'create'
    const [saving, setSaving] = useState(false);

    const toggleDeleteModal = () => {
        setDeleteModal(!deleteModal);
    };

    const statusActive = (id) => {
        // Store current state for potential rollback
        const previousNotifications = [...data];

        // Optimistically update UI immediately
        setData(prevNotifications =>
            prevNotifications.map(item =>
                item.id === id
                    ? { ...item, isActive: !item.isActive }
                    : item
            )
        );

        // Then make API call
        toggleAutoNotificationStatus(id)((response) => {
            if (response?.data?.statusCode !== 200 && response?.status !== 200) {
                setData(previousNotifications);
                toast.error("Failed to update notification status");
            } else {
                toast.success("Notification status updated successfully");
            }
        });
    }

    // Handle row click - open in view mode
    const handleRowClick = (row) => {
        setSelectedNotification(row);
        setNotificationModal(true);
    };

    // Handle edit button click
    const handleEditClick = () => {
        setModalMode('edit');
    };

    const canUpdateNotification = (notification) => {
        if (!notification) return false;

        const now = new Date();
        const scheduledTime = new Date(notification?.scheduledDateTime);
        const status = getNotificationStatus(notification);

        // Allow updates only for upcoming/scheduled notifications that haven't passed yet
        return (status === 'upcoming' || status === 'scheduled') && scheduledTime > now;
    };

    // Handle create new notification
    const handleCreateNotification = () => {
        // Set current date and time as default
        const now = new Date();
        const currentDate = now.toISOString().split('T')[0];
        const currentTime = now.toTimeString().slice(0, 5);

        setSelectedNotification({
            title: '',
            message: '',
            scheduledDate: currentDate,
            scheduledTime: currentTime,
            notificationType: '',
            isActive: true,
            navigateToPage: ''
        });
        setModalMode('create');
        setNotificationModal(true);
    };

    const handleFieldChange = (field, value) => {
        setSelectedNotification(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveNotification = () => {
        if (!selectedNotification) return;

        // Basic validation
        if (!selectedNotification.title?.trim()) {
            toast.error('Please enter a title');
            return;
        }
        if (!selectedNotification.message?.trim()) {
            toast.error('Please enter a message');
            return;
        }
        if (!selectedNotification.scheduledDate) {
            toast.error('Please select a scheduled date');
            return;
        }
        if (!selectedNotification.scheduledTime) {
            toast.error('Please select a scheduled time');
            return;
        }

        // Validate date/time is not in past
        const scheduledDateTime = new Date(`${selectedNotification.scheduledDate}T${selectedNotification.scheduledTime}`);
        if (scheduledDateTime < new Date()) {
            toast.error('Cannot schedule notification for past date/time');
            return;
        }

        setSaving(true);

        // Use same API for both create and update
        scheduleAutoNotification(selectedNotification)((response) => {
            setSaving(false);
            if (response?.status === 200 || response?.statusCode === 200 || response?.statusCode === 201) {
                toast.success('Notification saved successfully!');
                setUpdate(!update);
                setNotificationModal(false);
                setSelectedNotification(null);
            } else {
                toast.error(response?.data?.message || response?.message || 'Failed to save notification');
            }
        });
    };

    const handleCancel = () => {
        setNotificationModal(false);
        setSelectedNotification(null);
    };

    const deleteNotificationItem = (id) => {
        // deleteNotification(id)((response) => {
        //     if (response?.statusCode === 200) {
        //         toast.success('Notification deleted successfully!');
        //         setDeleteModal(false);
        //         setUpdate(!update);
        //     } else {
        //         toast.error(response?.message || 'Failed to delete notification');
        //     }
        // });
    }

    // Function to determine notification status for filtering
    const getNotificationStatus = (row) => {
        const now = new Date();
        const scheduledTime = new Date(row?.scheduledDateTime);

        if (row?.status?.toLowerCase() === "pending") {
            if (scheduledTime && scheduledTime > now) {
                return "upcoming";
            } else if (scheduledTime && scheduledTime < now) {
                return "expired";
            }
        }
        return row?.status?.toLowerCase();
    };

    // Check if active column should be shown based on current tab
    const shouldShowActiveColumn = () => {
        return activeTab === 'upcoming' || activeTab === 'scheduled';
    };

    // Combined title and message column
    const notificationContentColumn = {
        id: "notificationContent",
        name: "Notification Content",
        cell: (row) => (
            <div
                className="text-center"
                style={{ width: "100%", cursor: "pointer", padding: "8px" }}
                onClick={() => handleRowClick(row)}
            >
                <Tooltip
                    title={
                        <div>
                            <div><strong>Title:</strong> {row?.title}</div>
                            <div><strong>Message:</strong> {row?.message}</div>
                        </div>
                    }
                    disableInteractive
                    placement="top"
                >
                    <div>
                        <div className="fw-semibold text-truncate mx-auto" style={{ maxWidth: "200px" }}>
                            {row?.title}
                        </div>
                        <div className="text-muted text-truncate mx-auto" style={{ maxWidth: "200px" }}>
                            {row?.message}
                        </div>
                    </div>
                </Tooltip>
            </div>
        ),
        sortable: true,
        minWidth: "200px",
        grow: 2,
    };
    // Status column
    const statusColumn = {
        id: "status",
        name: "Status",
        selector: (row) => row?.status,
        cell: (row) => {
            const now = new Date();
            const scheduledTime = new Date(row?.scheduledDateTime);
            let displayStatus = row?.status;

            if (row?.status?.toLowerCase() === "pending") {
                if (scheduledTime && scheduledTime > now) {
                    displayStatus = "Upcoming";
                } else if (scheduledTime && scheduledTime < now) {
                    displayStatus = "Expired";
                }
            }

            const getStatusStyle = (status) => {
                switch (status?.toLowerCase()) {
                    case "upcoming": return { backgroundColor: "#28a745", color: "#fff" };
                    case "expired": return { backgroundColor: "#dc3545", color: "#fff" };
                    case "sent": return { backgroundColor: "#00b06aff", color: "#fff" };
                    case "failed": return { backgroundColor: "#6c757d", color: "#fff" };
                    case "scheduled": return { backgroundColor: "#008695ff", color: "#fff" };
                    default: return { backgroundColor: "#adb5bd", color: "#fff" };
                }
            };

            return (
                <div
                    className="text-center"
                    onClick={() => handleRowClick(row)}
                    style={{ cursor: "pointer" }}
                >
                    <span
                        className="badge px-3 py-2 fw-semibold"
                        style={{
                            ...getStatusStyle(displayStatus),
                            borderRadius: "6px",
                            fontSize: "0.75rem",
                            textTransform: "capitalize",
                        }}
                    >
                        {displayStatus}
                    </span>
                </div>
            );
        },
        sortable: true,
        width: "150px",
        center: true,
    };

    // Active status column (only shown for upcoming and scheduled tabs)
    const activeStatusColumn = {
        id: "isActive",
        name: "Active",
        cell: (row, index) => (
            <div className="d-flex justify-content-center">
                <div className="d-flex align-items-center">
                    <div className="d-flex flex-row align-items-center gap-3" role="button">
                        <div className="d-flex justify-content-center">
                            <Switch
                                size="20px"
                                id={`custom-switch-${index}`}
                                checked={row?.isActive}
                                onChange={() => statusActive(row?.id)}
                                className="custom-switch"
                            />
                        </div>
                    </div>
                </div>
            </div>
        ),
        center: true,
        width: "100px",
    };

    // Date column
    const dateColumn = {
        id: "scheduledDate",
        name: "Date",
        selector: (row) => row?.scheduledDate,
        cell: (row) => {
            const date = row?.scheduledDateTime ? new Date(row.scheduledDateTime) : null;
            return (
                <div
                    className="text-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(row)}
                >
                    {date ? (
                        <span className="fw-semibold">
                            {date.toLocaleDateString([], {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                            })}
                        </span>
                    ) : (
                        <span className="text-muted">-</span>
                    )}
                </div>
            );
        },
        sortable: true,
        width: "120px",
        center: true,
    };

    // Time column
    const timeColumn = {
        id: "scheduledTime",
        name: "Time",
        selector: (row) => row?.scheduledTime,
        cell: (row) => {
            const date = row?.scheduledDateTime ? new Date(row.scheduledDateTime) : null;
            return (
                <div
                    className="text-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(row)}
                >
                    {date ? (
                        <span>
                            {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                    ) : (
                        <span className="text-muted">-</span>
                    )}
                </div>
            );
        },
        sortable: true,
        width: "110px",
        center: true,
    };

    // Navigation column
    const navigationColumn = {
        id: "navigateToPage",
        name: "Navigation",
        selector: (row) => row?.navigateToPage,
        cell: (row) => (
            <div
                className="text-center"
                style={{ cursor: "pointer" }}
                onClick={() => handleRowClick(row)}
            >
                {row?.navigateToPage ? (
                    <span className="d-flex align-items-center justify-content-center gap-1">
                        <span className="fw-semibold text-truncate" style={{ maxWidth: "120px" }}>
                            {row.navigateToPage}
                        </span>
                    </span>
                ) : (
                    <span className="text-muted">-</span>
                )}
            </div>
        ),
        sortable: true,
        width: "150px",
        center: true,
    };

    // Dynamic columns based on active tab
    const getColumns = () => {
        const baseColumns = [
            {
                name: "S.No.",
                selector: (row, index) => (page - 1) * limit + (index + 1),
                width: "80px",
                center: true,
                style: { textAlign: "center" },
            },
            notificationContentColumn,
            statusColumn,
            dateColumn,
            timeColumn,
            navigationColumn,
        ];

        // Only add active column for upcoming and scheduled tabs
        if (shouldShowActiveColumn()) {
            baseColumns.splice(5, 0, activeStatusColumn); // Insert after status column
        }

        return baseColumns;
    };

    const getNotificationData = async () => {
        setLoading(true);
        const filters = {
            page: page,
            limit: limit,
            search: search,
            sortBy: sortBy,
            sortDirection: sortDirection,
            status: activeTab === 'all' ? null : activeTab // Send null for 'all' to get all statuses
        };

        getAllNotification(filters)((response) => {
            if (response?.statusCode == 200) {
                const notifications = response?.data?.notifications || [];
                const total = response?.data?.totalItems || 0;

                setData(notifications);
                setTotalRows(total);
            } else {
                setData([]);
            }
            setLoading(false);
        });
    }

    useEffect(() => {
        getNotificationData();
        setIsInitialRender(false);
    }, [update, page, limit, sortBy, sortDirection, activeTab]);

    useEffect(() => {
        let timeout = setTimeout(() => {
            if (initial) setUpdate(!update);
            else setInitial(true);
        }, 500);

        return () => {
            clearTimeout(timeout);
        };
    }, [search]);

    useEffect(() => {
        if (!isInitialRender) {
            setPage(1);
            getNotificationData();
        }
    }, [activeTab]);

    const handleSort = (column, sortDirection) => {
        setSortBy(column.id);
        setSortDirection(sortDirection);
        setUpdate(!update);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setLimit(newPerPage);
        setPage(1);
        setUpdate(!update);
    };

    const handlePageChange = (page) => {
        setPage(page);
        setUpdate(!update);
    };

    return (
        <>
            <div>
                <div className="row">
                    <div className="col-12">
                        <div className="page-header-sticky d-flex justify-content-between align-items-stretch mb-3">
                            <div className="d-flex align-items-center">
                                <h3 className="mb-0 fw-bolder">Auto Notifications</h3>
                            </div>

                            <div className="d-flex align-items-center pe-0">
                                <div className="d-flex align-items-center">
                                    <Button
                                        className='request_btn me-2'
                                        onClick={() => setShowBulkModal(true)}
                                        variant="outline-secondary"
                                    >
                                        <FiFilePlus className='icon' />Add Bulk Notifications
                                    </Button>
                                    <Button
                                        className='request_btn'
                                        onClick={handleCreateNotification}
                                    >
                                        <FiCalendar className='icon' />Schedule Notification
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs Navigation */}
                    <div className="my-1">
                        <Nav
                            variant="tabs"
                            defaultActiveKey="upcoming"
                            activeKey={activeTab}
                            onSelect={setActiveTab}
                            className="mb-2 custom-order-tabs"
                        >
                            <Nav.Item>
                                <Nav.Link
                                    eventKey="upcoming"
                                    className={`fs-7 fw-semibold ${activeTab === 'upcoming' ? 'fw-bold text-dark active-tab' : 'text-secondary'}`}
                                >
                                    <FiClock className="me-1" />
                                    Upcoming
                                </Nav.Link>
                            </Nav.Item>

                            <Nav.Item>
                                <Nav.Link
                                    eventKey="scheduled"
                                    className={`fs-7 fw-semibold ${activeTab === 'scheduled' ? 'fw-bold text-dark active-tab' : 'text-secondary'}`}
                                >
                                    <FiClock className="me-1" />
                                    Scheduled
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link
                                    eventKey="sent"
                                    className={`fs-7 fw-semibold ${activeTab === 'sent' ? 'fw-bold text-dark active-tab' : 'text-secondary'}`}
                                >
                                    <FiCheckCircle className="me-1" />
                                    Sent
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link
                                    eventKey="failed"
                                    className={`fs-7 fw-semibold ${activeTab === 'failed' ? 'fw-bold text-dark active-tab' : 'text-secondary'}`}
                                >
                                    <FiXCircle className="me-1" />
                                    Failed
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link
                                    eventKey="expired"
                                    className={`fs-7 fw-semibold ${activeTab === 'expired' ? 'fw-bold text-dark active-tab' : 'text-secondary'}`}
                                >
                                    <FiXCircle className="me-1" />
                                    Expired
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </div>

                    {/* Delete Confirmation Modal */}
                    <CustomisedModal
                        isOpen={deleteModal}
                        customClass={"policy_modal"}
                        toggle={toggleDeleteModal}
                    >
                        <h5>Are you sure you want to delete this notification?</h5>
                        <div className="policy_Agree">
                            <Button
                                onClick={() => setDeleteModal(!deleteModal)}
                                size="lg"
                                className='btn_cancel'
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="lg"
                                className='btn'
                                onClick={() => deleteNotificationItem(deletingId)}
                                disabled={saving}
                            >
                                {saving ? 'Deleting...' : 'Delete'}
                            </Button>
                        </div>
                    </CustomisedModal>

                    <Box className="products-and-options" sx={{ mt: 1 }}>
                        <Paper square className='shadow-none border border-blue-grey-200'>
                            <div>
                                <DataTable
                                    columns={getColumns()}
                                    data={data}
                                    progressPending={loading}
                                    progressComponent={<div style={{ height: '60vh' }}><Loader /></div>}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRows}
                                    paginationRowsPerPageOptions={[10, 50, 100, 150]}
                                    paginationPerPage={limit}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    onChangePage={handlePageChange}
                                    onSort={handleSort}
                                    sortServer
                                    currentPage={page}
                                    customStyles={queueDatatableCustomStyles}
                                    onRowClicked={handleRowClick}
                                    pointerOnHover
                                    highlightOnHover
                                />
                            </div>
                        </Paper>
                    </Box>
                </div>
            </div>

            <CreateUpdateAutoNotificationModal
                show={notificationModal}
                onHide={handleCancel}
                notification={selectedNotification}
                onSave={handleSaveNotification}
                onCancel={handleCancel}
                onFieldChange={handleFieldChange}
                saving={saving}
            />

            <BulkNotificationModal
                show={showBulkModal}
                onHide={() => setShowBulkModal(false)}
                onUpload={() => setUpdate(!update)}
            />
        </>
    )
}

export default AutoNotification;